import { useEffect, useRef } from 'react';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import { getVehiclePickerHistory, savePersistedState, getIndex } from '../components/car-picker-helpers';
import { carPickerAnalyticsEvent } from '../analytics';

export const useAutoPopulateDropDowns = ({
  dispatch, selectedOption, fitmentOptions, variables, appliedDimensions, optionTypes, yearData
}) => {
  const [makeAndModelRequest, makeAndModelResponse] = useLazyDataModel('fitmentOptionsV1', {
    ssr: false,
    variables
  });

  const { currentVehicleSelection } = getVehiclePickerHistory();
  let autoPopulateFilters = useRef({});
  let autoPopulateFitmentOptions = useRef({});
  let autoPopulateSelectedOptions = useRef({});
  let initValidationCompleted = useRef(false);
  const staticLabels = ['Year', 'Make', 'Model'];
  const staticKeys = ['YEAR', 'MAKE', 'MODEL'];
  const fitmentType = 'AUTOMOTIVE';

  const autoPopulateDropDowns = (updatedFilter, updatedSelectedOptions, updatedFitmentOptions) => {
    dispatch({
      action: 'SET_FILTER',
      value: { ...updatedFilter }
    });
    dispatch({
      action: 'SET_SELECTED_OPTIONS',
      value: { ...updatedSelectedOptions }
    });
    dispatch({
      action: 'SET_OPTIONS',
      value: { ...updatedFitmentOptions }
    });
    initValidationCompleted.current = true;

    const { YEAR, MAKE, MODEL } = updatedSelectedOptions;

    // Analytics event
    const analyticsData = {
      year: YEAR?.displayValue,
      yearIndex: getIndex(YEAR?.id, updatedFitmentOptions?.YEAR),
      make: MAKE?.displayValue,
      makeIndex: getIndex(MAKE?.id, updatedFitmentOptions?.MAKE),
      model: MODEL?.displayValue,
      modelIndex: getIndex(MODEL?.id, updatedFitmentOptions?.MODEL)
    };
    carPickerAnalyticsEvent('page-load', analyticsData);
    // End analytics event
  };

  const validateSelectedOption = (dropdownSelection, dropdownOptions = []) => {
    if (dropdownSelection) {
      const { displayValue, id } = dropdownSelection;
      const existingSelection = dropdownOptions.find((dropdownOption) => {
        return dropdownOption.value === displayValue && dropdownOption.id === id;
      });
      if (existingSelection) {
        return existingSelection;
      }
    }
    return undefined;
  };

  const runValidationOnStaticDropDowns = () => {
    if (yearData?.fitmentOptionsV1?.options?.length) {
      const existingSelection = validateSelectedOption(
        autoPopulateSelectedOptions.current.YEAR, yearData?.fitmentOptionsV1?.options
      );

      if (existingSelection) {
        autoPopulateFitmentOptions.current = { ...fitmentOptions };
        // Fire off lazyDataModel calls to get MAKE & MODEL dropdown options
        makeAndModelRequest({
          variables: {
            fitmentType,
            optionType: 'MAKE',
            filter: { year: autoPopulateFilters.current.year }
          }
        });
      } else if (appliedDimensions.length) {
        initValidationCompleted.current = true;
        savePersistedState({ ...autoPopulateSelectedOptions.current });
      }
    }
  };

  const getFiltersAndOptionsFromDimensions = () => {
    let mapKeys = {};
    let selectedOptions = {};
    let filters = {};

    optionTypes.forEach(({ value, id }) => {
      mapKeys[value] = {
        optionKey: id?.toUpperCase(),
        option: id
      };
    });

    const keys = Object.keys(mapKeys);

    appliedDimensions.forEach(({ label, refinements }) => {
      if (keys.includes(label)) {
        const { optionKey, option } = mapKeys[label];
        const { label: displayValue, refinementKey: id } = refinements?.[0];
        selectedOptions = {
          ...selectedOptions,
          [optionKey]: { id, displayValue, option }
        };
        // Update filters for dynamic dropdowns
        if (staticLabels.includes(label)) {
          filters = { ...filters, [option]: id };
        }
      }
    });

    return { selectedOptions, filters };
  };

  const useSelectedOptionsEffect = (dropDownOptionTypes = [], yearDropDownOptions) => {
    const calledOnce = useRef(false);

    useEffect(() => {
      if (calledOnce.current) {
        return;
      }
      if (Object.keys(dropDownOptionTypes).length && yearDropDownOptions?.fitmentOptionsV1?.options) {
        // URL path params hold precedence over localStorage so check this first
        if (appliedDimensions.length) {
          const { selectedOptions, filters } = getFiltersAndOptionsFromDimensions();
          autoPopulateSelectedOptions.current = { ...selectedOptions };
          autoPopulateFilters.current = { ...filters };

          runValidationOnStaticDropDowns();
        } else if (currentVehicleSelection) {
          autoPopulateSelectedOptions.current = { ...currentVehicleSelection };
          // Update filters for dynamic dropdowns
          staticKeys.forEach((key) => {
            if (currentVehicleSelection[key]?.id) {
              const { id, option } = currentVehicleSelection[key];
              autoPopulateFilters.current = { ...autoPopulateFilters.current, [option]: id };
            }
          });

          runValidationOnStaticDropDowns();
        } else {
          carPickerAnalyticsEvent('page-load');
        }
        calledOnce.current = true;
      }
    }, [dropDownOptionTypes, yearDropDownOptions]);
  };

  useSelectedOptionsEffect(optionTypes, yearData);

  const useMakeAndModelEffect = (dependencies, data, optionKey) => {
    const calledOnce = useRef(false);

    useEffect(() => {
      if (calledOnce.current) {
        return;
      }

      if (data && optionKey) {
        autoPopulateFitmentOptions.current = {
          ...autoPopulateFitmentOptions.current,
          [optionKey]: data
        };
        const existingSelection = validateSelectedOption(
          autoPopulateSelectedOptions.current?.[optionKey], data
        );

        if (existingSelection) {
          if (optionKey === 'MAKE') {
            const { year, make } = autoPopulateFilters.current;

            makeAndModelRequest({
              variables: {
                fitmentType,
                optionType: 'MODEL',
                filter: { year, make }
              }
            });
          } else if (optionKey === 'MODEL') {
            if (appliedDimensions.length) {
              savePersistedState({ ...autoPopulateSelectedOptions.current });
            }
            // If this is reached then Year, Make & Model are verified so update state which will trigger
            //  dynamic options to fire off
            autoPopulateDropDowns(
              autoPopulateFilters.current,
              autoPopulateSelectedOptions.current,
              autoPopulateFitmentOptions.current
            );
            dispatch({ action: 'TRIGGER_FITMENT_ITEMS_CALLBACK', value: true });
            calledOnce.current = true;
          }
        } else {
          // TODO: Maybe auto-select if data contains 1 item. Need to check if it breaks path manipulation
          const updatedFitmentOptions = { ...fitmentOptions, ...autoPopulateFitmentOptions.current };
          const { year, make } = autoPopulateFilters.current;
          const { YEAR, MAKE } = { ...autoPopulateSelectedOptions.current };
          let updatedFilter = { year };
          let updatedSelectedOptions = { ...selectedOption, YEAR };

          if (optionKey === 'MODEL') {
            updatedFilter.make = make;
            updatedSelectedOptions.MAKE = MAKE;
          }

          // Update localStorage for future path manipulation
          savePersistedState({ ...autoPopulateSelectedOptions.current });
          autoPopulateDropDowns(updatedFilter, updatedSelectedOptions, updatedFitmentOptions);
          calledOnce.current = true;
        }
      }
    }, [dependencies]);
  };

  useMakeAndModelEffect(
    makeAndModelResponse?.data?.fitmentOptionsV1,
    makeAndModelResponse?.data?.fitmentOptionsV1?.options,
    makeAndModelResponse?.variables?.optionType
  );

  useEffect(() => {
    // TODO: Updating selected options to align with chips will open the door to filtering
    // options independent of Year, Make, and Model
    // In Legacy, the entire car picker selection is grouped into a single chip so once that
    // is deselected by the user, then all the dropdowns are removed and revert to initial state
    if (!initValidationCompleted.current) {
      return;
    }
    const { selectedOptions } = getFiltersAndOptionsFromDimensions();
    savePersistedState({ ...selectedOptions });

  }, [appliedDimensions]);
};